import React, { useState } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { MUTATION_TYPE, useMutateData } from "hooks";
import { CheckBox, LAYOUT_DIRECTION } from "components/Form";
import { TextLoader, Spinner } from "components/Loader";
import { DestructableLink, PrimaryLink } from "components/Links";
import { colors, fontSize, margins } from "style";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import { Col } from "components/Containers";
import Tooltip from "components/Tooltip";
import { TemplateLegend } from "components/Legend";

/**
 * Helper function to format label text with styled template variables
 * @param {string} label - The label text that may contain {{variable}} patterns
 * @param {Array} templateVariables - Array of template variable objects with key, label, and description
 * @returns {React.Node} - Formatted label with styled variables
 */
const formatLabelWithVariables = (label, templateVariables = []) => {
  if (typeof label !== "string") return label;

  // Split by template variable pattern {{variable_name}}
  const parts = label.split(/(\{\{[^}]+\}\})/g);

  // If no variables found, return original label
  if (parts.length === 1) return label;

  return parts.map((part, index) => {
    // Check if this part is a template variable
    if (part.match(/\{\{[^}]+\}\}/)) {
      const variableKey = part.replace(/{|}/g, "");
      const templateVar = templateVariables.find((v) => v.key === variableKey);

      return (
        <Tooltip key={index} tooltipId={templateVar.description}>
          <code css={styles.code_block}>{part}</code>
        </Tooltip>
      );
    }

    return part;
  });
};

/**
 * Creates a group of checkboxes
 *
 * @param {Function}   onChange
 * @param {Boolean}    value
 * @param {String}     name
 * @param {Array}      options
 * @param {Boolean}    loading
 * @param {String}     valueKey
 * @param {String}     labelKey - Field to store in form data (plain string)
 * @param {String}     displayKey - Field to use for display (can be JSX), falls back to labelKey
 * @param {Function}   labelFunc
 * @param {String}     descriptionKey
 * @param {Object}     mutationData
 * @param {Number}     limit
 * @param {String}     direction
 * @param {Boolean}    showAllOptions
 * @param {Array}      templateVariables
 */
const CheckBoxGroup = ({
  onChange,
  value,
  name,
  options,
  loading,
  valueKey,
  labelKey,
  displayKey,
  labelFunc,
  descriptionKey,
  mutationData,
  limit,
  direction,
  showAllOptions,
  templateVariables,
  ...props
}) => {
  const [localLimit, setLocalLimit] = useState(limit);
  const incrementLimitSize = limit;
  const { handleDelete, loading: deleting } = useMutateData(mutationData, MUTATION_TYPE.delete);

  const getCheckboxLabel = (item) => {
    // Use displayKey for rendering if provided (can be JSX), otherwise fall back to labelKey
    const rawLabel = typeof labelFunc === "function" ? labelFunc(item) : item[displayKey] || item[labelKey];
    return formatLabelWithVariables(rawLabel, templateVariables);
  };

  const getRawLabel = (item) => {
    return typeof labelFunc === "function" ? labelFunc(item) : item[labelKey];
  };

  const onChangeLocal = (isChecked, selectedItem) => {
    onChange(
      name,
      isChecked
        ? [...value, { [labelKey]: getRawLabel(selectedItem), [valueKey]: selectedItem[valueKey] }]
        : value?.filter((item) => item[valueKey] !== selectedItem[valueKey])
    );
  };

  const handleRemove = (e, id) => {
    e.preventDefault();
    handleDelete({ id });
  };

  if (loading) {
    return [0, 1, 2, 3, 4, 5, 6].map((item, index) => (
      <div css={styles.checkbox_container} key={`${item}-${index}`}>
        <CheckBox
          key={item}
          label={
            <TextLoader
              css={css`
                width: 15rem;
              `}
            />
          }
          value={false}
        />
      </div>
    ));
  }

  return (
    <div css={styles.container}>
      {templateVariables?.length && <TemplateLegend variables={templateVariables} />}
      <Col gap={direction === LAYOUT_DIRECTION.horizontal ? "5rem" : "0.5rem"} direction={direction}>
        {options?.slice(0, showAllOptions ? options.length : localLimit).map((item, index) => {
          const selected = value?.filter((x) => x[valueKey] === item[valueKey]);
          const isSelected = selected?.length > 0;

          return (
            <div css={styles.checkbox_container} key={item[valueKey]}>
              <CheckBox
                id={`radio-${name}-${index}`}
                label={getCheckboxLabel(item)}
                description={item[descriptionKey]}
                value={isSelected}
                onChange={(n, isChecked) => onChangeLocal(isChecked, item)}
                name={name}
                {...props}
              />
              {item.isRemovable && (
                <span css={styles.remove_button}>
                  (
                  <DestructableLink onClick={(e) => handleRemove(e, item.id)} disabled={loading} withDeleteIcon>
                    <FormattedMessage id="Global.Remove" />
                  </DestructableLink>
                  )
                </span>
              )}
            </div>
          );
        })}
        {!showAllOptions && options?.length > localLimit && (
          <div css={styles.secondary}>
            {deleting && <Spinner size={`2rem`} />}
            <MoreLink onClick={() => setLocalLimit(localLimit + incrementLimitSize)}>
              <FormattedMessage id="Global.ShowMore" />
              <Icon type={ICON_TYPE.chevronDown} size={ICON_SIZE.xsmall} color={"inherit"} />
            </MoreLink>
          </div>
        )}
      </Col>
    </div>
  );
};

const styles = {
  container: css`
    order: 1;
  `,
  checkbox_container: css`
    display: flex;
    order: 1;
    gap: ${margins.xxsmall};
    align-items: center;
    color: ${colors.grayAnatomyLight1};
    max-height: 15rem;
    overflow-y: auto;
  `,
  remove_button: css`
    order: 2;
    min-width: 77px;
  `,
  secondary: css`
    order: 2;
    text-align: center;
  `,
  code_block: css`
    background-color: #f4f4f4;
    border: 1px solid #e0e0e0;
    padding: 2px 6px;
    border-radius: 3px;
    color: ${colors.moderateRed};
    font-weight: 500;
  `,
};

const MoreLink = styled(PrimaryLink)`
  margin-top: 0.5rem;
  font-size: ${fontSize.xsmall};
  border-top: 1px dashed ${colors.grayAnatomyLight3};
  display: block;
  padding-top: 0.5rem;

  .link-text {
    display: flex;
    flex-direction: column;
  }
`;

CheckBoxGroup.defaultProps = {
  value: [],
  valueKey: "id",
  labelkey: "label",
  displayKey: null,
  mutationData: {},
  limit: 30,
  showAllOptions: false,
};

CheckBoxGroup.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.array,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  valueKey: PropTypes.string,
  labelKey: PropTypes.string,
  displayKey: PropTypes.string,
  labelFunc: PropTypes.func,
  descriptionKey: PropTypes.string,
  mutationData: PropTypes.object,
  limit: PropTypes.number,
  direction: PropTypes.string,
  showAllOptions: PropTypes.bool,
  templateVariables: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
};

export default CheckBoxGroup;
